import React, { useState } from 'react';
import styles from './clientFcbDocument.module.css';
import formStyles from '../../../../components/Form/form.module.css';
import HookForm from '../../../../components/HookForm/HookForm';
import Button from '../../../../components/Button/Button';
import Hint from '../../../../components/Hint';
import pkbFcbSendCode from '../../../../api/clients/pkbFcbSendCode';
import pkbFcbGetDocument from '../../../../api/clients/pkbFcbGetDocument';
import alert from '../../../../helpers/alert';
import { textStyles } from '../../../../constants/textStyles';

const smsCodeMask = [/\d/, /\d/, /\d/, /\d/, /\d/, /\d/];

const howItWorksHint = (
<div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 220 }}>
<div style={{ display: 'flex', gap: 8 }}>
<span style={{ fontWeight: 700, color: '#b8894f' }}>1.</span>
<span>Отправляем SMS-код клиенту на указанный номер</span>
</div>
<div style={{ display: 'flex', gap: 8 }}>
<span style={{ fontWeight: 700, color: '#b8894f' }}>2.</span>
<span>Клиент называет код из SMS</span>
</div>
<div style={{ display: 'flex', gap: 8 }}>
<span style={{ fontWeight: 700, color: '#b8894f' }}>3.</span>
<span>Данные удостоверения личности подгрузятся в форму документов</span>
</div>
</div>
);

const ClientFcbDocument = ({ clientId, iin, phone, onSuccess, embedded }) => {
const [phase, setPhase] = useState('idle');
const [requestId, setRequestId] = useState(null);
const [code, setCode] = useState('');
const [errorMessage, setErrorMessage] = useState('');
const [expired, setExpired] = useState(false);
const [result, setResult] = useState(null);

const iinDigits = (iin || '').replace(/\D/g, '');
const phoneDigits = (phone || '').replace(/\D/g, '');
const canStart = iinDigits.length === 12 && phoneDigits.length > 0;

const sendCode = async () => {
if (!canStart) {
alert.error('Для запроса документа нужны корректные ИИН и телефон клиента');
return;
}
setPhase('sending');
setErrorMessage('');
setExpired(false);
try {
const res = await pkbFcbSendCode(clientId);
if (res?.data?.success) {
setRequestId(res.data.requestId);
setCode('');
setPhase('codeSent');
} else {
setErrorMessage(res?.data?.errorMessage || 'Не удалось отправить SMS-код');
setPhase('error');
}
} catch (e) {
setErrorMessage(e?.response?.data?.errorMessage || 'Не удалось отправить SMS-код');
setPhase('error');
}
};

const confirmCode = async () => {
const digits = (code || '').replace(/\D/g, '');
if (digits.length !== 6) {
alert.error('Введите 6-значный код из SMS');
return;
}
setPhase('checking');
setErrorMessage('');
try {
const res = await pkbFcbGetDocument(clientId, { requestId, code: digits });
if (res?.data?.success) {
setResult(res.data);
setPhase('success');
alert.success(res.data.message || 'Документ получен');
onSuccess && onSuccess();
} else {
const message = res?.data?.errorMessage || 'Не удалось получить документ';
setErrorMessage(message);
setExpired(/просрочен|истёк|истек/i.test(message));
setPhase('error');
}
} catch (e) {
const message = e?.response?.data?.errorMessage || 'Не удалось получить документ';
setErrorMessage(message);
setExpired(/просрочен|истёк|истек/i.test(message));
setPhase('error');
}
};

const retry = () => {
setPhase('idle');
setErrorMessage('');
setCode('');
};

const codeInputs = [
{
name: 'code',
label: 'Код из SMS',
placeholder: '_ _ _ _ _ _',
mask: smsCodeMask,
},
];

return (
<div
className={embedded ? undefined : formStyles.borderWrapper}
style={embedded ? { marginBottom: 16 } : { margin: 0 }}
>
{!embedded && (
<div style={{ display: 'flex', alignItems: 'center', columnGap: 20, marginBottom: 16 }}>
<p style={textStyles.formTitle}>
Получение документов клиента
<Hint text={howItWorksHint} />
</p>
</div>
)}

{phase === 'idle' && (
<div className={styles.row}>
<p className={styles.hintText}>
Запросим у клиента SMS-код и заполним данные удостоверения личности автоматически
</p>
<Button text="Отправить SMS-код" fill onClick={sendCode} disabled={!canStart} />
{!canStart && <p className={styles.warnText}>Проверьте ИИН и телефон клиента в блоке выше</p>}
</div>
)}

{phase === 'sending' && (
<div className={styles.row}>
<span className={styles.spinner} />
<p className={styles.hintText}>Отправляем SMS-код…</p>
</div>
)}

{(phase === 'codeSent' || phase === 'checking') && (
<div className={styles.row}>
<HookForm
inputs={codeInputs}
grid={{ display: 'grid', rowGap: 12, margin: 0, maxWidth: 220 }}
style={{ margin: 0, padding: 0, backgroundColor: 'transparent' }}
onValuesChange={(values) => setCode(values.code)}
/>
<Button
text="Подтвердить код"
fill
onClick={confirmCode}
disabled={phase === 'checking' || (code || '').replace(/\D/g, '').length !== 6}
/>
<span className={styles.manualLink} onClick={sendCode}>
Отправить код повторно
</span>
</div>
)}

{phase === 'success' && (
<div className={styles.row}>
<p className={styles.successText}>
Документ получен{result?.documentNumber ? `: № ${result.documentNumber}` : ''}
</p>
{result?.updatedFields?.length > 0 && (
<p className={styles.hintText}>Обновлены поля: {result.updatedFields.join(', ')}</p>
)}
<span className={styles.manualLink} onClick={retry}>
Запросить документ ещё раз
</span>
</div>
)}

{phase === 'error' && (
<div className={styles.row}>
<p className={styles.errorText}>{errorMessage}</p>
<Button text={expired ? 'Запросить новый код' : 'Попробовать ещё раз'} fill onClick={retry} />
</div>
)}
</div>
);
};

export default ClientFcbDocument;